import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import SchoolAutocomplete from "@/components/SchoolAutocomplete";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";

export default function SignUp() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [studentId, setStudentId] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("student");
  const [school, setSchool] = useState(null);
  const [licenseFile, setLicenseFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!school) return toast.error("Select a registered school");
    if (role === "teacher" && !licenseFile) return toast.error("Upload your teacher's license");
    setBusy(true);
    try {
      let teacherLicensePath = null;
      if (role === "teacher" && licenseFile) {
        const form = new FormData();
        form.append("file", licenseFile);
        const { data } = await api.post("/upload/public", form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        teacherLicensePath = data.path;
      }
      await register({
        name,
        email,
        password,
        role,
        school_id: school.id,
        student_id: studentId || null,
        teacher_license_path: teacherLicensePath,
      });
      toast.success("Account created! Welcome to Cure8r.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-16">
      <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Step 01</div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">Create your account</h1>
      <p className="text-zinc-400 mt-3">Join your school's creator economy. Students or teachers welcome.</p>

      <form onSubmit={submit} className="mt-10 space-y-6">
        <div>
          <Label className="text-zinc-300">I am a…</Label>
          <RadioGroup value={role} onValueChange={setRole} className="grid grid-cols-2 gap-3 mt-2">
            <label data-testid="role-student-option" className={`p-4 rounded-md border cursor-pointer ${role === "student" ? "border-cyan8r bg-cyan8r/5" : "border-white/10"}`}>
              <RadioGroupItem value="student" className="sr-only" />
              <div className="font-display text-lg">Student</div>
              <div className="text-xs text-zinc-500 mt-1">Mint & sell what you create</div>
            </label>
            <label data-testid="role-teacher-option" className={`p-4 rounded-md border cursor-pointer ${role === "teacher" ? "border-cyan8r bg-cyan8r/5" : "border-white/10"}`}>
              <RadioGroupItem value="teacher" className="sr-only" />
              <div className="font-display text-lg">Teacher</div>
              <div className="text-xs text-zinc-500 mt-1">Manage classes, approve mints</div>
            </label>
          </RadioGroup>
        </div>

        <div>
          <Label className="text-zinc-300">Full name</Label>
          <Input data-testid="signup-name" value={name} onChange={(e) => setName(e.target.value)} required className="bg-[#0A0A0A] border-white/10 mt-1" />
        </div>

        <div>
          <Label className="text-zinc-300">School</Label>
          <div className="mt-1">
            <SchoolAutocomplete value={school} onChange={setSchool} testId="signup-school" />
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            School not listed?{" "}
            <Link to="/register-school" className="text-cyan8r">Register it</Link>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-zinc-300">Email</Label>
            <Input data-testid="signup-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="bg-[#0A0A0A] border-white/10 mt-1" />
          </div>
          {role === "student" && (
            <div>
              <Label className="text-zinc-300">Student ID# (optional)</Label>
              <Input data-testid="signup-studentid" value={studentId} onChange={(e) => setStudentId(e.target.value)} className="bg-[#0A0A0A] border-white/10 mt-1" />
            </div>
          )}
        </div>

        <div>
          <Label className="text-zinc-300">Password</Label>
          <Input data-testid="signup-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="bg-[#0A0A0A] border-white/10 mt-1" />
        </div>

        {role === "teacher" && (
          <div>
            <Label className="text-zinc-300">Teacher's License</Label>
            <label className="mt-1 flex items-center gap-3 border border-dashed border-white/10 hover:border-cyan8r/40 rounded-md px-4 py-4 cursor-pointer bg-[#0A0A0A]">
              <Upload size={16} className="text-zinc-400" />
              <span className="text-sm text-zinc-300">
                {licenseFile ? licenseFile.name : "Upload license document (PDF, image)"}
              </span>
              <input data-testid="signup-license" type="file" className="hidden" accept="image/*,application/pdf" onChange={(e) => setLicenseFile(e.target.files?.[0] || null)} />
            </label>
            <div className="text-xs text-zinc-500 mt-1">You'll be auto-verified once uploaded (MVP).</div>
          </div>
        )}

        <Button
          data-testid="signup-submit"
          type="submit"
          disabled={busy}
          className="w-full bg-white text-black hover:bg-zinc-200 h-11"
        >
          {busy ? <><Loader2 className="animate-spin mr-2" size={16} /> Creating…</> : "Create Account"}
        </Button>
      </form>
    </div>
  );
}
